import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";

// The public marketing pages are what most visitors land on, so the shell and
// the home page stay in the main bundle. Everything else is split out.
import { HomePage } from "./pages/HomePage";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { RouteFallback } from "./components/RouteFallback";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ApplicationProvider } from "./contexts/ApplicationContext";
import { ContactSubmissionsProvider } from "./contexts/ContactSubmissionsContext";

// --- Public pages -----------------------------------------------------------
const AboutPage = lazy(() => import("./pages/AboutPage").then((m) => ({ default: m.AboutPage })));
const TreatmentsPage = lazy(() => import("./pages/TreatmentsPage").then((m) => ({ default: m.TreatmentsPage })));
const ServicesPage = lazy(() => import("./pages/ServicesPage").then((m) => ({ default: m.ServicesPage })));
const ContactPage = lazy(() => import("./pages/ContactPage").then((m) => ({ default: m.ContactPage })));
const FreeConsultationPage = lazy(() => import("./pages/FreeConsultationPage").then((m) => ({ default: m.FreeConsultationPage })));
const AyurvedaPage = lazy(() => import("./pages/AyurvedaPage").then((m) => ({ default: m.AyurvedaPage })));
const SurgeryPage = lazy(() => import("./pages/SurgeryPage").then((m) => ({ default: m.SurgeryPage })));
const YogaPage = lazy(() => import("./pages/YogaPage").then((m) => ({ default: m.YogaPage })));
const ConsultationPage = lazy(() => import("./pages/ConsultationPage").then((m) => ({ default: m.ConsultationPage })));
const PatientJourneyPage = lazy(() => import("./pages/PatientJourneyPage").then((m) => ({ default: m.PatientJourneyPage })));
const BlogPage = lazy(() => import("./pages/BlogPage").then((m) => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage").then((m) => ({ default: m.BlogPostPage })));

// --- Legal ------------------------------------------------------------------
const PrivacyPolicy = lazy(() => import("./components/legal/PrivacyPolicy").then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("./components/legal/TermsOfService").then((m) => ({ default: m.TermsOfService })));
const MedicalDisclaimer = lazy(() => import("./components/legal/MedicalDisclaimer").then((m) => ({ default: m.MedicalDisclaimer })));

// --- Admin portal -----------------------------------------------------------
// None of this belongs in an anonymous visitor's download.
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard").then((m) => ({ default: m.AdminDashboard })));
const AdminPatients = lazy(() => import("./components/admin/AdminPatients").then((m) => ({ default: m.AdminPatients })));
const AdminPatientDetails = lazy(() => import("./components/admin/AdminPatientDetails").then((m) => ({ default: m.AdminPatientDetails })));
const AdminVisaApplications = lazy(() => import("./components/admin/AdminVisaApplications").then((m) => ({ default: m.AdminVisaApplications })));
const AdminApplicationDetails = lazy(() => import("./components/admin/AdminApplicationDetails").then((m) => ({ default: m.AdminApplicationDetails })));
const AdminDocumentVerification = lazy(() => import("./components/admin/AdminDocumentVerification").then((m) => ({ default: m.AdminDocumentVerification })));
const AdminHospitals = lazy(() => import("./components/admin/AdminHospitals").then((m) => ({ default: m.AdminHospitals })));
const AdminHospitalForm = lazy(() => import("./components/admin/AdminHospitalForm").then((m) => ({ default: m.AdminHospitalForm })));
const AdminPayments = lazy(() => import("./components/admin/AdminPayments").then((m) => ({ default: m.AdminPayments })));
const AdminSupportQueries = lazy(() => import("./components/admin/AdminSupportQueries").then((m) => ({ default: m.AdminSupportQueries })));
const AdminReports = lazy(() => import("./components/admin/AdminReports").then((m) => ({ default: m.AdminReports })));
const AdminSettings = lazy(() => import("./components/admin/AdminSettings").then((m) => ({ default: m.AdminSettings })));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin").then((m) => ({ default: m.AdminLogin })));
const AdminFreeConsultations = lazy(() => import("./components/admin/AdminFreeConsultations").then((m) => ({ default: m.AdminFreeConsultations })));
const AdminContactRequests = lazy(() => import("./components/admin/AdminContactRequests").then((m) => ({ default: m.AdminContactRequests })));
const ProtectedAdminRoute = lazy(() => import("./components/admin/ProtectedAdminRoute").then((m) => ({ default: m.ProtectedAdminRoute })));

// --- Customer portal --------------------------------------------------------
const CustomerLayout = lazy(() => import("./components/customer/CustomerLayout").then((m) => ({ default: m.CustomerLayout })));
const CustomerDashboard = lazy(() => import("./components/customer/CustomerDashboard").then((m) => ({ default: m.CustomerDashboard })));
const CustomerProfile = lazy(() => import("./components/customer/CustomerProfile").then((m) => ({ default: m.CustomerProfile })));
const CustomerRegister = lazy(() => import("./components/customer/CustomerRegister").then((m) => ({ default: m.CustomerRegister })));
const ApplyMedicalVisa = lazy(() => import("./components/customer/ApplyMedicalVisa").then((m) => ({ default: m.ApplyMedicalVisa })));
const MedicalDetails = lazy(() => import("./components/customer/MedicalDetails").then((m) => ({ default: m.MedicalDetails })));
const HospitalSelection = lazy(() => import("./components/customer/HospitalSelection").then((m) => ({ default: m.HospitalSelection })));
const UploadDocuments = lazy(() => import("./components/customer/UploadDocuments").then((m) => ({ default: m.UploadDocuments })));
const ApplicationStatus = lazy(() => import("./components/customer/ApplicationStatus").then((m) => ({ default: m.ApplicationStatus })));
const ApplicationDetails = lazy(() => import("./components/customer/ApplicationDetails").then((m) => ({ default: m.ApplicationDetails })));
const CustomerApplicationDetails = lazy(() => import("./components/customer/CustomerApplicationDetails").then((m) => ({ default: m.CustomerApplicationDetails })));
const PaymentDetails = lazy(() => import("./components/customer/PaymentDetails").then((m) => ({ default: m.PaymentDetails })));
const SupportHelp = lazy(() => import("./components/customer/SupportHelp").then((m) => ({ default: m.SupportHelp })));
const SupportDetails = lazy(() => import("./components/customer/SupportDetails").then((m) => ({ default: m.SupportDetails })));
const CustomerLogin = lazy(() => import("./components/customer/CustomerLogin").then((m) => ({ default: m.CustomerLogin })));
const ProtectedCustomerRoute = lazy(() => import("./components/customer/ProtectedCustomerRoute").then((m) => ({ default: m.ProtectedCustomerRoute })));

/** Legal pages share the public chrome. */
const LegalPage = ({ children }) => (
  <>
    <Header onContactClick={() => { }} />
    <main className="pt-20">{children}</main>
    <Footer />
  </>
);

function App() {

  return (
    <ApplicationProvider>
      <ContactSubmissionsProvider>
        <div className="App">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout />
                  </ProtectedAdminRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="patients" element={<AdminPatients />} />
                <Route path="patients/:id" element={<AdminPatientDetails />} />
                <Route path="visa-applications" element={<AdminVisaApplications />} />
                <Route path="visa-applications/:id" element={<AdminApplicationDetails />} />
                <Route path="document-verification" element={<AdminDocumentVerification />} />
                <Route path="hospitals" element={<AdminHospitals />} />
                <Route path="hospitals/new" element={<AdminHospitalForm />} />
                <Route path="hospitals/:id" element={<AdminHospitalForm />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="support-queries" element={<AdminSupportQueries />} />
                <Route path="free-consultations" element={<AdminFreeConsultations />} />
                <Route path="free-consultations/pending" element={<AdminFreeConsultations statusFilter="NEW" />} />
                <Route path="free-consultations/scheduled" element={<AdminFreeConsultations statusFilter="CONTACTED" />} />
                <Route path="free-consultations/rejected" element={<AdminFreeConsultations statusFilter="CLOSED" />} />
                <Route path="free-consultations/completed" element={<AdminFreeConsultations statusFilter="IN_PROGRESS" />} />
                <Route path="contact-requests" element={<AdminContactRequests />} />
                <Route path="contact-requests/new" element={<AdminContactRequests statusFilter="NEW" />} />
                <Route path="contact-requests/contacted" element={<AdminContactRequests statusFilter="CONTACTED" />} />
                <Route path="contact-requests/in-progress" element={<AdminContactRequests statusFilter="IN_PROGRESS" />} />
                <Route path="contact-requests/closed" element={<AdminContactRequests statusFilter="CLOSED" />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="/customer/login" element={<CustomerLogin />} />
              <Route path="/customer/register" element={<CustomerRegister />} />
              <Route
                path="/customer"
                element={
                  <ProtectedCustomerRoute>
                    <CustomerLayout />
                  </ProtectedCustomerRoute>
                }
              >
                <Route index element={<CustomerDashboard />} />
                <Route path="profile" element={<CustomerProfile />} />
                <Route path="apply-visa" element={<ApplyMedicalVisa />} />
                <Route path="medical-details" element={<MedicalDetails />} />
                <Route path="hospital-selection" element={<HospitalSelection />} />
                <Route path="upload-documents" element={<UploadDocuments />} />
                <Route path="application-status" element={<ApplicationStatus />} />
                <Route path="application/:id" element={<ApplicationDetails />} />
                <Route path="application-details/:id" element={<CustomerApplicationDetails />} />
                <Route path="payment-details" element={<PaymentDetails />} />
                <Route path="support" element={<SupportHelp />} />
                <Route path="support/:id" element={<SupportDetails />} />
              </Route>

              {/* Portfolio Pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/treatments" element={<TreatmentsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/treatments/ayurveda" element={<AyurvedaPage />} />
              <Route path="/treatments/surgery" element={<SurgeryPage />} />
              <Route path="/treatments/yoga" element={<YogaPage />} />
              <Route path="/treatments/consultation" element={<ConsultationPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/free-consultation" element={<FreeConsultationPage />} />
              <Route path="/patient-journey" element={<PatientJourneyPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:id" element={<BlogPostPage />} />
              {/* Redirects for combined pages */}
              <Route path="/why-us" element={<Navigate to="/services" replace />} />

              {/* Legal Pages */}
              <Route path="/privacy-policy" element={<LegalPage><PrivacyPolicy /></LegalPage>} />
              <Route path="/terms-of-service" element={<LegalPage><TermsOfService /></LegalPage>} />
              <Route path="/medical-disclaimer" element={<LegalPage><MedicalDisclaimer /></LegalPage>} />

              {/* Anything else. Without this, an unknown path rendered a blank
                  page, because the server now serves index.html for every URL. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>

          <Toaster richColors position="top-right" />
        </div>
      </ContactSubmissionsProvider>
    </ApplicationProvider>
  );
}

export default App;
